import {
    Button,
    DisplayOrInput,
    Input,
    Modal,
    ModalFooter,
    ModalHeader,
    CodebookType,
    SearchableDropdown,
    Field,
} from "modules/common";
import { useContext, useEffect, useMemo, useState } from "react";
import { CodebookEntry } from "services/codebooks";
import {
    useCreateCodebookEntry,
    useEditCodebookEntry,
    useGetProfessionalAreasAsItems,
} from "../hooks/codebooksHooks";
import { ReactComponent as SearchIcon } from "assets/icons/search.svg";
import { AccessibilityContext } from "views/AccessibilityContext";

export interface ICodebookEntryDetailsModal {
    codebook?: CodebookEntry;
    isOpen: boolean;
    handleClose: () => void;
    codebookType: CodebookType;
}

const emptyCodebook: CodebookEntry = {
    id: 0,
    guid: "",
    name: "",
    code: "",
    targetGroup: "",
    professionalAreaGuid: "",
    professionalAreaName: "",
    created: "",
    createdBy: "",
    lastUpdated: "",
    lastUpdatedBy: "",
    codebookType: CodebookType.Unknown,
    isActive: true,
};

type CodebookEntryProperty = keyof CodebookEntry;
type CodebookEntryErrors = Partial<Record<CodebookEntryProperty, string>>;

const validateCodebook = (codebook: CodebookEntry): CodebookEntryErrors => {
    const errors: CodebookEntryErrors = {};
    if (!codebook.name?.trim()) {
        errors.name = "Naziv je obavezan";
    }
    if (codebook.codebookType === CodebookType.Intervention && !codebook.code?.trim()) {
        errors.code = "Kod je obavezan";
    }
    if (codebook.codebookType === CodebookType.Intervention && !codebook.targetGroup?.trim()) {
        errors.targetGroup = "Ciljna grupa je obavezna";
    }
    if (
        codebook.codebookType === CodebookType.ProductionType &&
        !codebook.professionalAreaGuid?.trim()
    ) {
        errors.professionalAreaGuid = "Stručno područje je obavezno";
    }
    return errors;
};

export const CodebookDetailsModal = ({
    codebook: selectedCodebook,
    isOpen,
    handleClose,
    codebookType,
}: ICodebookEntryDetailsModal) => {
    const [codebook, setCodebook] = useState<CodebookEntry>(emptyCodebook);
    const [errors, setErrors] = useState<CodebookEntryErrors>({});
    const [isEdit, setIsEdit] = useState(false);

    const create = useCreateCodebookEntry();
    const edit = useEditCodebookEntry();

    const professionalAreasAsItems = useGetProfessionalAreasAsItems();
    const professionalArea = useMemo(
        () => professionalAreasAsItems.find(item => item.guid === codebook.professionalAreaGuid),
        [professionalAreasAsItems, codebook.professionalAreaGuid]
    );
    const { lowVision } = useContext(AccessibilityContext);

    useEffect(() => {
        setCodebook(
            selectedCodebook
                ? { ...selectedCodebook, codebookType }
                : { ...emptyCodebook, codebookType }
        );
    }, [selectedCodebook, codebookType]);

    useEffect(() => {
        setErrors({});
        setIsEdit(false);
        if (!isOpen) {
            setCodebook({ ...emptyCodebook, codebookType });
        }
    }, [isOpen]);

    const isAdd = !selectedCodebook;

    const title = useMemo(() => {
        if (isAdd) {
            return "Novi šifrarnik";
        }
        if (isEdit) {
            return "Uredi šifrarnik";
        }
        return codebook?.name || "Detalji šifrarnika";
    }, [isAdd, isEdit, codebook?.name]);

    const changeCodebookProperty = (
        property: CodebookEntryProperty,
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        setErrors(prev => ({ ...prev, [property]: undefined }));
        setCodebook(prev => ({
            ...prev,
            [property]: event.target.value,
        }));
    };

    const isValid = (property: CodebookEntryProperty) => !errors[property];
    const getErrorMsg = (property: CodebookEntryProperty) => errors[property] ?? "";

    const onCancel = () => {
        setIsEdit(false);
        setErrors({});
        setCodebook(selectedCodebook ?? emptyCodebook);
    };

    const onSave = async () => {
        const newErrors = validateCodebook(codebook);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        const mutation = isAdd ? create.mutateAsync : edit.mutateAsync;
        await mutation(codebook);
        handleClose();
    };

    return (
        <Modal
            isActive={isOpen}
            className="codebook-details-modal"
            onClose={handleClose}
            modalHeader={<ModalHeader onClose={handleClose} title={title} />}
            modalFooter={
                <ModalFooter>
                    {isAdd || isEdit ? (
                        <>
                            {isEdit && (
                                <Button
                                    type="button"
                                    size="small"
                                    variant="primary-ghost"
                                    lowVision={lowVision}
                                    onClick={onCancel}
                                >
                                    <span>ODUSTANI</span>
                                </Button>
                            )}
                            <Button
                                type="button"
                                size="small"
                                variant="primary"
                                lowVision={lowVision}
                                onClick={onSave}
                            >
                                <span>SPREMI</span>
                            </Button>
                        </>
                    ) : (
                        <Button
                            type="button"
                            size="small"
                            variant="primary-ghost"
                            lowVision={lowVision}
                            onClick={() => setIsEdit(true)}
                        >
                            <span>UREDI</span>
                        </Button>
                    )}
                </ModalFooter>
            }
        >
            <div className="codebook-details">
                <div className="modal-section">
                    <Field name="Naziv" errorMessage={getErrorMsg("name")}>
                        <DisplayOrInput isEdit={isAdd || isEdit}>
                            {codebook.name}
                            <Input
                                id="name"
                                type="text"
                                size="small"
                                name="name"
                                isValid={isValid("name")}
                                value={codebook.name}
                                onChange={e => changeCodebookProperty("name", e)}
                            />
                        </DisplayOrInput>
                    </Field>
                </div>

                {codebookType === CodebookType.Intervention && (
                    <div className="modal-section">
                        <Field name="Kod" errorMessage={getErrorMsg("code")}>
                            <DisplayOrInput isEdit={isAdd || isEdit}>
                                {codebook.code}
                                <Input
                                    id="code"
                                    type="text"
                                    size="small"
                                    name="code"
                                    isValid={isValid("code")}
                                    value={codebook.code}
                                    onChange={e => changeCodebookProperty("code", e)}
                                />
                            </DisplayOrInput>
                        </Field>
                    </div>
                )}

                {codebookType === CodebookType.Intervention && (
                    <div className="modal-section">
                        <Field name="Ciljna grupa" errorMessage={getErrorMsg("targetGroup")}>
                            <DisplayOrInput isEdit={isAdd || isEdit}>
                                {codebook.targetGroup}
                                <Input
                                    id="targetGroup"
                                    type="text"
                                    size="small"
                                    name="targetGroup"
                                    isValid={isValid("targetGroup")}
                                    value={codebook.targetGroup}
                                    onChange={e => changeCodebookProperty("targetGroup", e)}
                                />
                            </DisplayOrInput>
                        </Field>
                    </div>
                )}

                {codebookType === CodebookType.ProductionType && (
                    <div className="modal-section">
                        <Field
                            name="Stručno područje"
                            errorMessage={getErrorMsg("professionalAreaGuid")}
                        >
                            <DisplayOrInput isEdit={isAdd || isEdit}>
                                {codebook.professionalAreaName}
                                <SearchableDropdown
                                    items={professionalAreasAsItems}
                                    value={professionalArea}
                                    noOptionsMessage={() => "Nema pronađenih stručnih područja"}
                                    labelKey="label"
                                    onSelect={item =>
                                        setCodebook({
                                            ...codebook,
                                            professionalAreaGuid: item?.guid ?? "",
                                            professionalAreaName: item?.label ?? "",
                                        })
                                    }
                                    placeholder="Pretraživanje"
                                    customIcons={{
                                        searchIcon: <SearchIcon width={14} height={14} />,
                                    }}
                                    isValid={isValid("professionalAreaGuid")}
                                />
                            </DisplayOrInput>
                        </Field>
                    </div>
                )}
            </div>
        </Modal>
    );
};
